import { Dispatch, SetStateAction, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, DateRangePicker, FilterContext, Flex, Select } from "dash-ui";
import { PropertyChangelogTypesEnum } from "dash-common";

import {
  PROPERTIES_SORT_VALUES,
  TrackedPropertiesSortFields,
  TrackedPropertiesSortOptionsEnum,
} from "./FilterFields.constants";

const FilterFields = ({
  isDrawerFilter,
  newDrawerFilters,
  setFilter,
}: {
  isDrawerFilter?: boolean;
  newDrawerFilters?: Record<string, any>;
  setFilter?: Dispatch<SetStateAction<Record<string, any>>>;
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const { setFilterState, state: filterState } = useContext(FilterContext);
  const [hasChangedSorting, setHasChangedSorting] = useState(false);

  const getDisplaySortValue = () => {
    const currentDrawerFilterValue = newDrawerFilters?.sort?.defaultValue;
    const filterStateValue = filterState?.sort?.defaultValue;

    if (hasChangedSorting) {
      return currentDrawerFilterValue;
    }

    return filterStateValue;
  };

  const setSortOptions = (value: TrackedPropertiesSortOptionsEnum) => {
    setHasChangedSorting(true);
    let sortOptions: TrackedPropertiesSortFields[];
    let defaultValue: TrackedPropertiesSortOptionsEnum | undefined;

    if (!value) {
      sortOptions = [
        PROPERTIES_SORT_VALUES[TrackedPropertiesSortOptionsEnum.CreatedAt],
      ];
    } else {
      sortOptions = [PROPERTIES_SORT_VALUES[value]];
      defaultValue = value;
    }

    return { sortOptions, defaultValue };
  };

  const setFiltersState = (key: string, value: any) => {
    const isSort = key === "sort";
    const { sortOptions, defaultValue } = isSort
      ? setSortOptions(value)
      : { sortOptions: [], defaultValue: value };
    const values = isSort ? { sortOptions, defaultValue } : value;

    if (isDrawerFilter && setFilter) {
      return setFilter((prev) => ({ ...prev, [key]: values }));
    }
    setFilterState(key, values);
  };

  return (
    <Flex
      alignItems="center"
      flexDirection={isDrawerFilter ? "column" : "row"}
      mt={[3, 0, 0, 0]}
    >
      <Box w={["100%", 239, 239, 259]} style={{ zIndex: 2 }}>
        <DateRangePicker
          placeholderText={t("pages:propertyPage.filterByTimeRange") || ""}
          startDate={filterState.propertyUpdatesRange?.startDate}
          endDate={filterState.propertyUpdatesRange?.endDate}
          onChange={(range) => setFiltersState("propertyUpdatesRange", range)}
          onClear={() => setFilterState("propertyUpdatesRange", null)}
          isClearable
        />
      </Box>
      <Select
        mx={2}
        my={[4, 0, 0, 0]}
        placeholder={t("common:labels.sortBy")}
        w={["100%", 161, 161, 161]}
        value={getDisplaySortValue()}
        isClearable
        isSearchable={false}
        options={Object.values(TrackedPropertiesSortOptionsEnum).map(
          (item) => ({
            label: t(`common:sortOptions.${item}`),
            value: item,
          })
        )}
        onChanged={(value) => setFiltersState("sort", value)}
      />
      <Select
        w={["100%", 161, 161, 161]}
        isMulti
        placeholder={t("common:labels.filterBy")}
        isClearable
        menuPlacementHorizontal={"right"}
        value={filterState?.propertyChangelogTypes}
        options={Object.values(PropertyChangelogTypesEnum).map((item) => ({
          label: t(`common:historyTypesMessages.${item}`, { folderName: "" }),
          value: item,
        }))}
        onChanged={(_, values) =>
          setFiltersState("propertyChangelogTypes", values)
        }
      />
    </Flex>
  );
};

export default FilterFields;
